'use client';

import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useCosmicEffects } from '@/hooks/use-cosmic-effects';
import {
    BOX_H,
    BOX_W,
    LINE_WIDTH,
    constellationFor,
    type ConstellationGeometry,
    type ConstellationStar,
} from '@/lib/seller-constellation';
import { cn } from '@/lib/utils';

/**
 * A faint constellation behind a seller's name: a few twinkling stars whose
 * connecting lines stay invisible until a slow shine sweeps across them now
 * and then, the whole figure drifting and rocking slightly in place. Shown on
 * the product page byline and the search page's seller cards, only while the
 * starfield itself is showing. The figure is the seller's own, seeded from
 * their uid (src/lib/seller-constellation.ts), so it is the same everywhere.
 *
 * Call-site pattern, and why each part matters:
 *
 *   <p className="relative ..."><SellerConstellation seed={id} /><span className="relative">{name}</span></p>
 *
 * - The SVG is absolutely positioned, so the parent must be `relative`, and
 *   it must not clip (a `truncate` on it would cut the figure to the text box;
 *   put that on the inner span).
 * - The name paints over the figure because it comes later in the DOM and is
 *   positioned too. Deliberately no negative z-index: the search card is an
 *   opaque surface that becomes a stacking context only while hovered (its
 *   translate), so a figure pushed below it would hide behind the card at
 *   rest and pop through on hover.
 * - The box starts 6px left of the name and no further: the avatar before it
 *   is positioned and earlier in the DOM, so a figure reaching across the gap
 *   would paint over its edge.
 *
 * GATED HERE, not at the call sites, so none can forget. The `stars` flag of
 * useCosmicEffects() folds in the viewer's toggle for the theme, a seller's
 * design override, reduced motion and checkout, and is false on the server and
 * the first client render, so there is no hydration mismatch.
 *
 * CHEAP BY CONSTRUCTION. CSS animations only, of transform on the <svg> (an
 * HTML-level box the compositor can move; transforms on SVG children resolve
 * their origin inconsistently across browsers) and opacity on its children.
 * The search page can hold hundreds of these, so one shared
 * IntersectionObserver pauses every figure that is off screen, and a figure
 * renders no stars at all until it has first come into view. The keyframes
 * live in globals.css inside the reduced-motion guard.
 */
export function SellerConstellation({ seed, className }: { seed: string | null | undefined; className?: string }) {
    const { stars } = useCosmicEffects();
    const geometry = useMemo(() => (stars ? constellationFor(seed) : null), [stars, seed]);
    if (!geometry) return null;
    return <ConstellationSvg geometry={geometry} className={className} />;
}

/* ------------------------------------------------------ off-screen pausing */

type Watcher = (onScreen: boolean) => void;

let sharedObserver: IntersectionObserver | null = null;
const watchers = new WeakMap<Element, Watcher>();

/**
 * Report when `el` enters or leaves the viewport (with a little margin, so a
 * figure is already running as it scrolls in). One observer for every figure
 * on the page rather than one each. Without IntersectionObserver the figure
 * simply always runs, which is how it would look anyway.
 */
function watchOnScreen(el: Element, onChange: Watcher): () => void {
    if (typeof IntersectionObserver === 'undefined') {
        onChange(true);
        return () => {};
    }
    if (!sharedObserver) {
        sharedObserver = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) watchers.get(entry.target)?.(entry.isIntersecting);
            },
            { rootMargin: '64px' },
        );
    }
    const observer = sharedObserver;
    watchers.set(el, onChange);
    observer.observe(el);
    return () => {
        watchers.delete(el);
        observer.unobserve(el);
    };
}

/* ------------------------------------------------------------------ markup */

const px = (v: number) => `${v}px`;
const ms = (v: number) => `${v}ms`;

function starStyle(s: ConstellationStar): CSSProperties {
    return {
        '--sc-lo': s.lo,
        '--sc-hi': s.hi,
        '--sc-ms': ms(s.twinkleMs),
        '--sc-delay': ms(s.twinkleDelayMs),
    } as CSSProperties;
}

function ConstellationSvg({ geometry, className }: { geometry: ConstellationGeometry; className?: string }) {
    const ref = useRef<SVGSVGElement>(null);
    const [onScreen, setOnScreen] = useState(false);
    const [seen, setSeen] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        return watchOnScreen(el, (visible) => {
            setOnScreen(visible);
            if (visible) setSeen(true);
        });
    }, []);

    const { drift } = geometry;
    const rootStyle = {
        '--sc-drift-ms': ms(drift.durationMs),
        '--sc-drift-delay': ms(drift.delayMs),
        '--sc-dx': px(drift.dx),
        '--sc-dy': px(drift.dy),
        '--sc-rot': `${drift.rot}deg`,
        '--sc-dx2': px(drift.dx2),
        '--sc-dy2': px(drift.dy2),
        '--sc-rot2': `${drift.rot2}deg`,
        '--sc-shine-ms': ms(geometry.shineMs),
        '--sc-peak': geometry.shinePeak,
    } as CSSProperties;

    // Resting values are presentation attributes (lines at 0, stars at their
    // low), which the CSS animations override while they run; with no
    // animation the lines are simply not there.
    return (
        <svg
            ref={ref}
            aria-hidden="true"
            focusable="false"
            viewBox={`0 0 ${BOX_W} ${BOX_H}`}
            width={BOX_W}
            height={BOX_H}
            data-paused={onScreen ? undefined : ''}
            // currentColor: white on the dark sky, ink on a light page (light
            // mode has no starfield since 2026-10-23, so this twinkles there
            // only if that ever changes, and white stars would vanish there).
            className={cn(
                'sc-root pointer-events-none absolute -left-1.5 top-1/2 -mt-6 select-none overflow-visible print:hidden text-zinc-800 dark:text-white',
                className,
            )}
            style={rootStyle}
        >
            {seen && (
                <>
                    <g stroke="currentColor" strokeWidth={LINE_WIDTH} strokeLinecap="round">
                        {geometry.lines.map((l) => (
                            <line
                                key={`${l.a}-${l.b}`}
                                className="sc-line"
                                x1={l.x1}
                                y1={l.y1}
                                x2={l.x2}
                                y2={l.y2}
                                opacity={0}
                                style={{ '--sc-delay': ms(l.shineDelayMs) } as CSSProperties}
                            />
                        ))}
                    </g>
                    <g fill="currentColor">
                        {geometry.dust.map((s, i) => (
                            <circle key={`d${i}`} className="sc-star" cx={s.x} cy={s.y} r={s.r} opacity={s.lo} style={starStyle(s)} />
                        ))}
                        {geometry.stars.map((s, i) => (
                            <circle key={`s${i}`} className="sc-star" cx={s.x} cy={s.y} r={s.r} opacity={s.lo} style={starStyle(s)} />
                        ))}
                    </g>
                </>
            )}
        </svg>
    );
}
